import React, { useEffect } from 'react';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider as MuiThemeProvider } from '@mui/material';
import AppRoutes from './routes';
import { StoreProvider } from './app/appContext';
import { setUnauthorizedHandler } from './api/authEvents';
import { ThemeProvider } from './contexts/ThemeContext';
import { I18nProvider } from './contexts/I18nContext';
import { useMiniAppAutoLogin } from './hooks/useMiniAppAutoLogin';
import theme from './theme';

function UnauthorizedHandlerBinder() {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = () => {
      try {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('jwt');
          localStorage.removeItem('refresh');
          localStorage.removeItem('username');
          localStorage.removeItem('me:last');
          localStorage.removeItem('zones:last');
        }
      } catch {}
      navigate('/login', { replace: true });
    };
    setUnauthorizedHandler(handler);
    return () => setUnauthorizedHandler(null);
  }, [navigate]);
  return null;
}

function MiniAppGate({ children }: { children: React.ReactNode }) {
  // In a Mini App container we want to swap the JWT (or land on /login)
  // BEFORE PrivateRoutes evaluates auth state — otherwise the user
  // briefly sees /login flash before the auto-auth roundtrip finishes.
  // In a normal browser this resolves true synchronously, so there's no
  // perceived delay outside the Mini App path.
  const { ready } = useMiniAppAutoLogin();
  if (!ready) return null;
  return <>{children}</>;
}

function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <StoreProvider>
          <MuiThemeProvider theme={theme}>
            <CssBaseline />
            <BrowserRouter>
              <UnauthorizedHandlerBinder />
              <MiniAppGate>
                <AppRoutes />
              </MiniAppGate>
            </BrowserRouter>
          </MuiThemeProvider>
        </StoreProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;
